import { jsx as _jsx } from "react/jsx-runtime";
import React, { createContext, useContext, useReducer, useMemo, useCallback, useRef, useEffect } from 'react';
import * as Cesium from 'cesium';
import { area } from '@turf/area';
import { polygon } from '@turf/helpers';
import '../../../_proto/landscope-map-viewer_pb';
import { grpcClientFactory } from '../../services/grpcClientFactory';
import { createBoundingPolygon } from '../../utils/polygonUtils';
import { getDemAoiMode } from '../../utils/demAoiMode';
import { clampPointToPolygon, parsePolygonRing } from '../../utils/geometryUtils';
// Initial state
const initialGeometryState = {
    points: [],
    pointEntities: [],
    polygonEntity: null,
    bufferEntity: null,
    isDragging: false,
    draggedPointIndex: -1,
    selectedVertexIndex: -1
};
const initialAddressState = {
    value: '',
    suggestions: [],
    isSearching: false,
    showSuggestions: false,
    selectedSuggestionIndex: -1
};
const initialAreaState = {
    data: null,
    isLoading: false
};
const initialUIState = {
    areaUnit: 'acres',
    distanceUnit: 'ft',
    showOrderModal: false,
    showBufferConfig: false,
    bufferDistanceMeters: 150,
    showSearchOnMobile: false,
    // Contour configuration - default unit matches area unit (acres = feet)
    showContourConfig: false,
    standardContourUnit: 'feet',
    customContours: []
};
const initialState = {
    geometry: initialGeometryState,
    address: initialAddressState,
    area: initialAreaState,
    ui: initialUIState
};
// Reducer
function drawingReducer(state, action) {
    var _a;
    switch (action.type) {
        case 'GEOMETRY_SET_POINTS':
            return {
                ...state,
                geometry: {
                    ...state.geometry,
                    points: action.points,
                    pointEntities: action.entities
                }
            };
        case 'GEOMETRY_ADD_POINT':
            return {
                ...state,
                geometry: {
                    ...state.geometry,
                    points: [...state.geometry.points, action.point],
                    pointEntities: [...state.geometry.pointEntities, action.entity]
                }
            };
        case 'GEOMETRY_INSERT_POINT': {
            const idx = Math.max(0, Math.min(action.index, state.geometry.points.length));
            const nextPoints = [...state.geometry.points];
            nextPoints.splice(idx, 0, action.point);
            const nextEntities = [...state.geometry.pointEntities];
            nextEntities.splice(idx, 0, action.entity);
            // Selection bookkeeping: inserted vertex becomes the active one.
            return {
                ...state,
                geometry: {
                    ...state.geometry,
                    points: nextPoints,
                    pointEntities: nextEntities,
                    selectedVertexIndex: idx
                }
            };
        }
        case 'GEOMETRY_REMOVE_POINT': {
            // Keep selectedVertexIndex consistent with the new array layout:
            // removing the selected vertex clears the selection; removing an
            // earlier vertex shifts the selected index down by one.
            const prevSelected = state.geometry.selectedVertexIndex;
            const nextSelected = prevSelected === action.index ? -1 :
                prevSelected > action.index ? prevSelected - 1 :
                    prevSelected;
            return {
                ...state,
                geometry: {
                    ...state.geometry,
                    points: state.geometry.points.filter((_, index) => index !== action.index),
                    pointEntities: state.geometry.pointEntities.filter((_, index) => index !== action.index),
                    selectedVertexIndex: nextSelected
                }
            };
        }
        case 'GEOMETRY_UPDATE_POINT':
            return {
                ...state,
                geometry: {
                    ...state.geometry,
                    points: state.geometry.points.map((point, index) => index === action.index ? action.point : point)
                }
            };
        case 'GEOMETRY_SET_DRAGGING':
            return {
                ...state,
                geometry: {
                    ...state.geometry,
                    isDragging: action.isDragging,
                    draggedPointIndex: (_a = action.pointIndex) !== null && _a !== void 0 ? _a : -1
                }
            };
        case 'GEOMETRY_SET_SELECTED_VERTEX':
            return {
                ...state,
                geometry: {
                    ...state.geometry,
                    selectedVertexIndex: action.index
                }
            };
        case 'GEOMETRY_SET_BUFFER_ENTITY':
            return {
                ...state,
                geometry: {
                    ...state.geometry,
                    bufferEntity: action.entity
                }
            };
        case 'ADDRESS_SET_VALUE':
            return {
                ...state,
                address: {
                    ...state.address,
                    value: action.value
                }
            };
        case 'ADDRESS_SET_SUGGESTIONS':
            return {
                ...state,
                address: {
                    ...state.address,
                    suggestions: action.suggestions
                }
            };
        case 'ADDRESS_SET_SEARCHING':
            return {
                ...state,
                address: {
                    ...state.address,
                    isSearching: action.isSearching
                }
            };
        case 'ADDRESS_SHOW_SUGGESTIONS':
            return {
                ...state,
                address: {
                    ...state.address,
                    showSuggestions: action.show
                }
            };
        case 'ADDRESS_SET_SELECTED':
            return {
                ...state,
                address: {
                    ...state.address,
                    selectedSuggestionIndex: action.index
                }
            };
        case 'AREA_SET_DATA':
            return {
                ...state,
                area: {
                    ...state.area,
                    data: action.data
                }
            };
        case 'AREA_SET_LOADING':
            return {
                ...state,
                area: {
                    ...state.area,
                    isLoading: action.isLoading
                }
            };
        case 'UI_SET_AREA_UNIT': {
            const metricArea = action.unit === 'hectares' || action.unit === 'sqm' || action.unit === 'sqkm';
            return {
                ...state,
                ui: {
                    ...state.ui,
                    areaUnit: action.unit,
                    standardContourUnit: metricArea ? 'meters' : 'feet'
                }
            };
        }
        case 'UI_SET_DISTANCE_UNIT':
            return {
                ...state,
                ui: {
                    ...state.ui,
                    distanceUnit: action.unit
                }
            };
        case 'UI_SHOW_ORDER_MODAL':
            return {
                ...state,
                ui: {
                    ...state.ui,
                    showOrderModal: action.show
                }
            };
        case 'UI_SHOW_BUFFER_CONFIG':
            return {
                ...state,
                ui: {
                    ...state.ui,
                    showBufferConfig: action.show
                }
            };
        case 'UI_SET_BUFFER_DISTANCE':
            return {
                ...state,
                ui: {
                    ...state.ui,
                    bufferDistanceMeters: action.distance
                }
            };
        case 'UI_TOGGLE_SEARCH_ON_MOBILE':
            return {
                ...state,
                ui: {
                    ...state.ui,
                    showSearchOnMobile: action.show
                }
            };
        case 'UI_SHOW_CONTOUR_CONFIG':
            return {
                ...state,
                ui: {
                    ...state.ui,
                    showContourConfig: action.show
                }
            };
        case 'UI_SET_STANDARD_CONTOUR_UNIT':
            return {
                ...state,
                ui: {
                    ...state.ui,
                    standardContourUnit: action.unit
                }
            };
        case 'UI_ADD_CUSTOM_CONTOUR':
            return {
                ...state,
                ui: {
                    ...state.ui,
                    customContours: [...state.ui.customContours, action.contour]
                }
            };
        case 'UI_REMOVE_CUSTOM_CONTOUR':
            return {
                ...state,
                ui: {
                    ...state.ui,
                    customContours: state.ui.customContours.filter((_, index) => index !== action.index)
                }
            };
        case 'UI_CLEAR_CUSTOM_CONTOURS':
            return {
                ...state,
                ui: {
                    ...state.ui,
                    customContours: []
                }
            };
        case 'RESET_ALL':
            return initialState;
        default:
            return state;
    }
}
// Helper function to calculate polygon area using Turf.js (accurate geodesic calculation)
function calculatePolygonArea(points) {
    if (points.length < 3)
        return 0;
    // Convert Cesium Cartesian3 points to longitude/latitude coordinates
    const coordinates = points.map(point => {
        const cartographic = Cesium.Cartographic.fromCartesian(point);
        return [
            Cesium.Math.toDegrees(cartographic.longitude), // longitude first (GeoJSON format)
            Cesium.Math.toDegrees(cartographic.latitude) // latitude second
        ];
    });
    // Ensure the polygon is closed (first and last points are the same)
    if (coordinates.length > 0) {
        const firstPoint = coordinates[0];
        const lastPoint = coordinates[coordinates.length - 1];
        if (firstPoint[0] !== lastPoint[0] || firstPoint[1] !== lastPoint[1]) {
            coordinates.push([...firstPoint]); // Close the polygon
        }
    }
    // Create a Turf polygon and calculate area
    try {
        const turfPolygon = polygon([coordinates]);
        return area(turfPolygon); // Returns area in square meters (geodesic calculation)
    }
    catch (error) {
        console.warn('Turf.js area calculation failed, falling back to 0:', error);
        return 0;
    }
}
// Helper function to convert area to acres
function squareMetersToAcres(squareMeters) {
    return squareMeters * 0.000247105;
}
// Context
const DrawingContext = createContext(null);
export const DrawingProvider = ({ children }) => {
    const [state, dispatch] = useReducer(drawingReducer, initialState);
    // Expose dispatch for one-shot bulk set by GeometryManager injection
    useEffect(() => {
        window.__drawingDispatch = dispatch;
        return () => { delete window.__drawingDispatch; };
    }, [dispatch]);
    // gRPC client for area calculations with compression
    const areaClient = useMemo(() => grpcClientFactory.createAreaClient({ enableCompression: true }), []);
    // Area calculation state
    const [grpcAreaData, setGrpcAreaData] = React.useState({
        polygonArea: 0,
        bufferArea: 0,
        areaInAcres: 0,
        bufferAreaInAcres: 0,
        loading: false
    });
    // Debounce timer for area calculations
    const areaCalculationDebounceRef = useRef(null);
    const calculateArea = useCallback(async (points, bufferDistanceMeters) => {
        if (points.length < 3) {
            setGrpcAreaData({
                polygonArea: 0,
                bufferArea: 0,
                areaInAcres: 0,
                bufferAreaInAcres: 0,
                loading: false,
                bufferCoordinates: undefined
            });
            return;
        }
        setGrpcAreaData(prev => ({ ...prev, loading: true }));
        dispatch({ type: 'AREA_SET_LOADING', isLoading: true });
        try {
            const orderedPoints = createBoundingPolygon(points);
            const coordinates = orderedPoints.map(point => {
                const cartographic = Cesium.Cartographic.fromCartesian(point);
                const Coordinate = proto.landscope.mapviewer.v1.Coordinate;
                const protoCoord = new Coordinate();
                protoCoord.setLatitude(Cesium.Math.toDegrees(cartographic.latitude));
                protoCoord.setLongitude(Cesium.Math.toDegrees(cartographic.longitude));
                return protoCoord;
            });
            const request = new proto.landscope.mapviewer.v1.CalculateAreaRequest();
            request.setCoordinatesList(coordinates);
            if (bufferDistanceMeters > 0) {
                request.setBufferMeters(bufferDistanceMeters);
            }
            const response = await new Promise((resolve, reject) => {
                areaClient.calculateArea(request, {}, (err, response) => {
                    if (err)
                        reject(err);
                    else
                        resolve(response);
                });
            });
            if (response.getSuccess()) {
                const polygonAreaInAcres = response.getProjectAreaAcres();
                const bufferAreaInAcres = response.getBufferAreaAcres();
                const serverBufferCoords = response.getBufferPolygonList();
                setGrpcAreaData({
                    polygonArea: polygonAreaInAcres * 4046.86,
                    bufferArea: bufferAreaInAcres * 4046.86,
                    areaInAcres: polygonAreaInAcres,
                    bufferAreaInAcres: bufferAreaInAcres,
                    loading: false,
                    bufferCoordinates: serverBufferCoords
                });
                dispatch({ type: 'AREA_SET_DATA', data: {
                        success: true,
                        message: response.getMessage(),
                        projectAreaAcres: polygonAreaInAcres,
                        bufferAreaAcres: bufferAreaInAcres
                    } });
                dispatch({ type: 'AREA_SET_LOADING', isLoading: false });
            }
            else {
                setGrpcAreaData({
                    polygonArea: 0,
                    bufferArea: 0,
                    areaInAcres: 0,
                    bufferAreaInAcres: 0,
                    loading: false,
                    bufferCoordinates: undefined
                });
                dispatch({ type: 'AREA_SET_DATA', data: {
                        success: false,
                        message: response.getMessage() || 'Server calculation failed',
                        projectAreaAcres: 0,
                        bufferAreaAcres: 0
                    } });
                dispatch({ type: 'AREA_SET_LOADING', isLoading: false });
            }
        }
        catch (error) {
            setGrpcAreaData({
                polygonArea: 0,
                bufferArea: 0,
                areaInAcres: 0,
                bufferAreaInAcres: 0,
                loading: false,
                bufferCoordinates: undefined
            });
            dispatch({ type: 'AREA_SET_DATA', data: {
                    success: false,
                    message: 'Network error',
                    projectAreaAcres: 0,
                    bufferAreaAcres: 0
                } });
            dispatch({ type: 'AREA_SET_LOADING', isLoading: false });
        }
    }, [areaClient]);
    const debouncedCalculateArea = useCallback((points, bufferDistanceMeters) => {
        if (areaCalculationDebounceRef.current) {
            clearTimeout(areaCalculationDebounceRef.current);
        }
        areaCalculationDebounceRef.current = setTimeout(() => {
            calculateArea(points, bufferDistanceMeters);
        }, 300);
    }, [calculateArea]);
    // Effect to trigger area calculation when geometry or buffer changes
    useEffect(() => {
        debouncedCalculateArea(state.geometry.points, state.ui.bufferDistanceMeters);
        return () => {
            if (areaCalculationDebounceRef.current) {
                clearTimeout(areaCalculationDebounceRef.current);
            }
        };
    }, [state.geometry.points, state.ui.bufferDistanceMeters, debouncedCalculateArea]);
    // Computed values - now using gRPC data
    const computed = useMemo(() => {
        const hasMinimumPoints = state.geometry.points.length >= 3;
        // Use reordered points for coordinates to match the visual polygon
        const orderedPoints = hasMinimumPoints ? createBoundingPolygon(state.geometry.points) : state.geometry.points;
        const coordinates = orderedPoints.map(point => {
            const cartographic = Cesium.Cartographic.fromCartesian(point);
            return {
                lat: Cesium.Math.toDegrees(cartographic.latitude),
                lng: Cesium.Math.toDegrees(cartographic.longitude)
            };
        });
        // Use server-calculated buffer coordinates (GEOS) - no fallback
        let bufferedCoordinates = (grpcAreaData.bufferCoordinates && grpcAreaData.bufferCoordinates.length > 0)
            ? grpcAreaData.bufferCoordinates.map((coord) => ({
                lat: coord.getLatitude(),
                lng: coord.getLongitude()
            }))
            : [];
        // DEM area-of-interest: confine the water-flow buffer to the uploaded DEM's
        // coverage so it never extends past the boundary — snap overshooting buffer
        // vertices onto the DEM outline. Drives both this preview and the bounds sent
        // on finish; normal (non-DEM) drawing is unaffected.
        const demAoi = getDemAoiMode();
        if (demAoi && bufferedCoordinates.length >= 3) {
            const demRing = parsePolygonRing(demAoi.demBoundsGeojson);
            if (demRing && demRing.length >= 3) {
                bufferedCoordinates = bufferedCoordinates.map((c) => clampPointToPolygon(c, demRing));
            }
        }
        return {
            hasMinimumPoints,
            polygonArea: grpcAreaData.polygonArea,
            areaInAcres: grpcAreaData.areaInAcres,
            bufferArea: grpcAreaData.bufferArea,
            bufferAreaInAcres: grpcAreaData.bufferAreaInAcres,
            coordinates,
            bufferedCoordinates,
            areaCalculationLoading: grpcAreaData.loading
        };
    }, [state.geometry.points, state.ui.bufferDistanceMeters, grpcAreaData]);
    // Actions - define callbacks at top level, not inside useMemo
    const addPoint = useCallback((point, entity) => {
        try {
            const carto = Cesium.Cartographic.fromCartesian(point);
        }
        catch (_a) { }
        dispatch({ type: 'GEOMETRY_ADD_POINT', point, entity });
    }, []);
    const insertPoint = useCallback((index, point, entity) => {
        dispatch({ type: 'GEOMETRY_INSERT_POINT', index, point, entity });
    }, []);
    // Replace the entire geometry point list. Used by the bridge in
    // DrawingOrchestrator to mirror usePolygonDrawing's cartesianPoints into
    // the context so downstream consumers (area calc, pricing, buffer math,
    // billing) keep reading from a single place.
    const setPoints = useCallback((points, entities) => {
        dispatch({ type: 'GEOMETRY_SET_POINTS', points, entities });
    }, []);
    const removePoint = useCallback((index) => {
        dispatch({ type: 'GEOMETRY_REMOVE_POINT', index });
    }, []);
    const updatePoint = useCallback((index, point) => {
        try {
            const carto = Cesium.Cartographic.fromCartesian(point);
        }
        catch (_a) { }
        dispatch({ type: 'GEOMETRY_UPDATE_POINT', index, point });
    }, []);
    const setDragging = useCallback((isDragging, pointIndex) => {
        dispatch({ type: 'GEOMETRY_SET_DRAGGING', isDragging, pointIndex });
    }, []);
    const setSelectedVertex = useCallback((index) => {
        dispatch({ type: 'GEOMETRY_SET_SELECTED_VERTEX', index });
    }, []);
    const setAddress = useCallback((value) => {
        dispatch({ type: 'ADDRESS_SET_VALUE', value });
    }, []);
    const setAddressSuggestions = useCallback((suggestions) => {
        dispatch({ type: 'ADDRESS_SET_SUGGESTIONS', suggestions });
    }, []);
    const setSearching = useCallback((isSearching) => {
        dispatch({ type: 'ADDRESS_SET_SEARCHING', isSearching });
    }, []);
    const showSuggestions = useCallback((show) => {
        dispatch({ type: 'ADDRESS_SHOW_SUGGESTIONS', show });
    }, []);
    const setSelectedSuggestion = useCallback((index) => {
        dispatch({ type: 'ADDRESS_SET_SELECTED', index });
    }, []);
    const setAreaData = useCallback((data) => {
        dispatch({ type: 'AREA_SET_DATA', data });
    }, []);
    const setAreaLoading = useCallback((isLoading) => {
        dispatch({ type: 'AREA_SET_LOADING', isLoading });
    }, []);
    const setAreaUnit = useCallback((unit) => {
        dispatch({ type: 'UI_SET_AREA_UNIT', unit });
    }, []);
    const setDistanceUnit = useCallback((unit) => {
        dispatch({ type: 'UI_SET_DISTANCE_UNIT', unit });
    }, []);
    const showOrderModal = useCallback((show) => {
        dispatch({ type: 'UI_SHOW_ORDER_MODAL', show });
    }, []);
    const showBufferConfig = useCallback((show) => {
        dispatch({ type: 'UI_SHOW_BUFFER_CONFIG', show });
    }, []);
    const setBufferDistance = useCallback((distance) => {
        dispatch({ type: 'UI_SET_BUFFER_DISTANCE', distance });
    }, []);
    const toggleSearchOnMobile = useCallback((show) => {
        dispatch({ type: 'UI_TOGGLE_SEARCH_ON_MOBILE', show });
    }, []);
    // Contour actions
    const showContourConfig = useCallback((show) => {
        dispatch({ type: 'UI_SHOW_CONTOUR_CONFIG', show });
    }, []);
    const setStandardContourUnit = useCallback((unit) => {
        dispatch({ type: 'UI_SET_STANDARD_CONTOUR_UNIT', unit });
    }, []);
    const addCustomContour = useCallback((contour) => {
        dispatch({ type: 'UI_ADD_CUSTOM_CONTOUR', contour });
    }, []);
    const removeCustomContour = useCallback((index) => {
        dispatch({ type: 'UI_REMOVE_CUSTOM_CONTOUR', index });
    }, []);
    const clearCustomContours = useCallback(() => {
        dispatch({ type: 'UI_CLEAR_CUSTOM_CONTOURS' });
    }, []);
    const setBufferEntity = useCallback((entity) => {
        dispatch({ type: 'GEOMETRY_SET_BUFFER_ENTITY', entity });
    }, []);
    const resetState = useCallback(() => {
        dispatch({ type: 'RESET_ALL' });
    }, []);
    // Actions object - now just references to the callbacks
    const actions = useMemo(() => ({
        // Geometry actions
        addPoint,
        insertPoint,
        removePoint,
        updatePoint,
        setDragging,
        setSelectedVertex,
        setPoints,
        // Address actions
        setAddress,
        setAddressSuggestions,
        setSearching,
        showSuggestions,
        setSelectedSuggestion,
        // Area actions
        setAreaData,
        setAreaLoading,
        // UI actions
        setAreaUnit,
        setDistanceUnit,
        showOrderModal,
        showBufferConfig,
        setBufferDistance,
        toggleSearchOnMobile,
        // Contour actions
        showContourConfig,
        setStandardContourUnit,
        addCustomContour,
        removeCustomContour,
        clearCustomContours,
        // Buffer actions
        setBufferEntity,
        // Global
        resetState
    }), [
        addPoint, insertPoint, removePoint, updatePoint, setDragging, setSelectedVertex, setPoints,
        setAddress, setAddressSuggestions, setSearching, showSuggestions, setSelectedSuggestion,
        setAreaData, setAreaLoading,
        setAreaUnit, setDistanceUnit, showOrderModal, showBufferConfig, setBufferDistance, toggleSearchOnMobile,
        showContourConfig, setStandardContourUnit, addCustomContour, removeCustomContour, clearCustomContours,
        setBufferEntity, resetState
    ]);
    // Memoize context value to prevent unnecessary re-renders
    const contextValue = useMemo(() => ({
        state,
        actions,
        computed
    }), [state, actions, computed]);
    return (_jsx(DrawingContext.Provider, { value: contextValue, children: children }));
};
// Hook to use the context
export const useDrawingContext = () => {
    const context = useContext(DrawingContext);
    if (!context) {
        throw new Error('useDrawingContext must be used within a DrawingProvider');
    }
    return context;
};
export default DrawingContext;
